import React, { createRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './SubNav.module.less';
import SubNavDropdown from './SubNavDropdown';
import SubNavButton from './SubNavButton';
import { useGlobalToggle, closeGlobalToggles } from 'edcom-ui';
import { tabInfoPropType, isSeasonalNavButtonActive } from '../util';
import clsx from 'clsx';

const createButtonContainerRefs = navInfo => {
  const initialValue = {};
  return navInfo.reduce(
    (obj, item) => ({ ...obj, [item.urlName]: createRef() }),
    initialValue
  );
};

/**
 * Basically, when we have a Seasonal Link active, the SubNav buttons get squished on narrow screens. To circumvent
 * this, we take the 2 last buttons before the "More" tab and add them to the "More" tab.
 */
const addButtonsToMoreTab = navInfo => {
  const moreTab = navInfo.find(tab => tab.name === 'More');
  if (!moreTab || !moreTab.sections) return;
  // Exit if we already added the buttons into navInfo
  if (moreTab.sections[0].items.find(item => item.button)) return;
  if (navInfo.length < 4) return;

  // Get the two buttons before the "More" tab. More tab will be second to last if there is a seasonal link, so we want
  // the 2 buttons before that. Is that an assumption that can might be wrong in the future? Yes. Is there a better
  // (and easy) way to implement the requirement of hiding buttons inside the "More" dropdown? Not that I know of.
  const buttons = navInfo.slice(navInfo.length - 4, navInfo.length - 2);

  for (let i = 0; i < buttons.length; i++) {
    const { name, urlName } = buttons[i];
    moreTab.sections[0].items.unshift({
      title: name,
      type: 'Main Item',
      column: 1,
      url: `/${urlName}/`,
      icon: '',
      button: true,
    });
  }
};

export default function SubNav({ navInfo }) {
  const [selectedSubNav, setSelectedSubNav] = useState(null);
  const dropdownContainerRef = useRef(null);
  // Use global toggle so other components (e.g. AutoSuggestWrapper) can close the suggestion programmatically
  const [isDropdownOpen, setIsDropdownOpen] = useGlobalToggle(
    dropdownContainerRef
  );
  const buttonContainerRefs = useRef(createButtonContainerRefs(navInfo));

  // Since both actions (opening and closing the modal) are delayed, we need to keep track of some timeoutIds
  const dropdownMenuTimeoutIds = useRef({ open: null, close: null });

  const clearTimeoutIfExists = type => {
    if (dropdownMenuTimeoutIds.current[type]) {
      clearTimeout(dropdownMenuTimeoutIds.current[type]);
      dropdownMenuTimeoutIds.current[type] = null;
    }
  };

  const updateSelectedSubNav = urlName =>
    setSelectedSubNav(navInfo.find(tab => tab.urlName === urlName));

  useEffect(() => {
    // Clear timeouts on component unmount to prevent memory leaks
    return () => {
      clearTimeoutIfExists('open');
      clearTimeoutIfExists('close');
    };
  }, []);

  const handleMouseLeave = async (withDelay = true) => {
    clearTimeoutIfExists('open');
    clearTimeoutIfExists('close');

    dropdownMenuTimeoutIds.current.close = setTimeout(
      () => {
        setIsDropdownOpen(false);

        // Set another timeout so that, when the slide up animation is done (after 200ms), we can then unset
        // selectedSubNav. We can't do if before the animation is done, because then the dropdown would disappear
        dropdownMenuTimeoutIds.current.close = setTimeout(() => {
          updateSelectedSubNav(null);
        }, 200);
      },
      withDelay ? 200 : 0
    );
  };

  const handleMouseEnter = async (subNavTab, withDelay = true) => {
    // If a SubNavTab has no dropdown, treat it like we're mousing out, so we get the slide up/down animation on any
    // currently-open dropdowns.
    if (subNavTab && !subNavTab.hasDropdown) {
      await handleMouseLeave();
      return;
    }

    clearTimeoutIfExists('close');

    // If the cursor goes from button->dropdown, dropdown->button, or button->button (i.e., not outside->button):
    if (selectedSubNav) {
      if (selectedSubNav.urlName !== subNavTab.urlName) {
        // If a user hovers over another SubNavButton, instantly update the selectedSubNav
        updateSelectedSubNav(subNavTab.urlName);
      }

      // Close other menus (userMenu, searchBar, etc.) that use the GlobalToggle hook
      closeGlobalToggles();
      // Set dropdown open to true, which is likely already the case, in case the menu is "closing" (so selectedSubNav
      // isn't null, but isDropdownOpen is false)
      setIsDropdownOpen(true);
      return;
    }

    clearTimeoutIfExists('open');
    updateSelectedSubNav(subNavTab.urlName);

    // If no subNav is open, wait 300ms, so we don't annoy users just passing the cursor through a SubNavButton
    dropdownMenuTimeoutIds.current.open = setTimeout(
      () => {
        // Close other menus (userMenu, searchBar, etc.) that use the GlobalToggle hook
        closeGlobalToggles();
        setIsDropdownOpen(true);
      },
      withDelay ? 300 : 0
    );
  };

  // Bool to see if there is an active seasonal button. This is used to change the styling on narrow widths
  const hasSeasonalButtonActive = navInfo.some(
    tabInfo =>
      tabInfo.seasonalInfo && isSeasonalNavButtonActive(tabInfo.seasonalInfo)
  );

  if (hasSeasonalButtonActive) addButtonsToMoreTab(navInfo);

  return (
    <nav className={styles.subNav} role="navigation" aria-label="Main">
      <div className={styles.container}>
        <div className={styles.subNavMore}>
          <div
            className={clsx(
              styles.subNavLinks,
              hasSeasonalButtonActive && styles.activeSeasonalButton
            )}
            onMouseLeave={handleMouseLeave}
          >
            <ul>
              {navInfo.map(subNavTab => (
                <li
                  className={styles.subNavButtonContainer}
                  key={subNavTab.urlName}
                  ref={buttonContainerRefs.current[subNavTab.urlName]}
                >
                  <SubNavButton
                    tabInfo={subNavTab}
                    openDropdown={handleMouseEnter}
                    closeDropdown={handleMouseLeave}
                    selected={
                      isDropdownOpen &&
                      selectedSubNav &&
                      selectedSubNav.urlName === subNavTab.urlName
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {selectedSubNav && selectedSubNav.hasDropdown && (
        <div className={styles.subNavContainer}>
          <div
            className={clsx(
              styles.dropdownContainer,
              isDropdownOpen ? styles.open : styles.closed
            )}
            ref={dropdownContainerRef}
            onMouseEnter={() => handleMouseEnter(selectedSubNav)}
            onMouseLeave={handleMouseLeave}
          >
            <SubNavDropdown
              key={selectedSubNav.urlName}
              tabInfo={selectedSubNav}
              closeDropdown={handleMouseLeave}
              buttonRefs={buttonContainerRefs.current}
            />
          </div>
        </div>
      )}
    </nav>
  );
}

SubNav.propTypes = {
  onClick: PropTypes.func,
  page: PropTypes.string,
  navInfo: PropTypes.arrayOf(tabInfoPropType),
};
